import { ComponentProps, PropsWithChildren, ReactNode, memo } from 'react';

import { AppBar, IconButton, Stack, Toolbar, Typography } from '@mui/material';
import { LogoContainer } from 'common/components';
import { useBreakpoints, useElementSize } from 'common/hooks';
import { CloseIcon } from 'icons';

import { CurrentUserContainer, GoToHelpCenter, MobileMenuButton, NavigationBarToolbar, PrintLogo } from './components';
import { NavigationBarDivider, SectionWrapper, TitleWrapper } from './NavigationBar.styled';

type Props = {
  variant?: 'default' | 'dark';
  title?: ReactNode;
  disableUserSection?: boolean;
  disableLogoInteraction?: boolean;
  onClose?(): void;
  ToolbarProps?: Omit<ComponentProps<typeof NavigationBarToolbar>, 'title' | 'children'>;
};

/**
 * @module **Natter Design System**
 *
 * **Natter navigation**
 *
 * A horizontal navigation component for Natter products.
 * @link https://www.figma.com/design/En2gOztv0xR9rIjYFtdut5/Design-system?node-id=343-2420
 *
 * @example
 * ```tsx
 * <NavigationBar />
 *
 *
 * <NavigationBar
 *   title="Untitled Event"
 *   ToolbarProps={{
 *     onExit: handleExit,
 *     action: <Button>Button</Button>,
 *     extraButtons: (
 *       <IconButton>
 *         <VisibilityOutlined />
 *       </IconButton>
 *     ),
 *   }}
 * />
 * ```
 **/
export const NavigationBar = memo(
  ({
    title,
    onClose,
    disableUserSection = false,
    disableLogoInteraction = false,
    ToolbarProps,
    variant = 'default',
    children,
  }: PropsWithChildren<Props>) => {
    const { isMobile } = useBreakpoints();

    const displayUserSection = !disableUserSection && !onClose;

    const [ref, size] = useElementSize();

    return (
      <>
        <PrintLogo />
        <AppBar
          component="header"
          position="fixed"
          color="inherit"
          ref={ref}
          sx={{ ...(variant === 'dark' && { backgroundColor: 'transparent', border: 'none' }), displayPrint: 'none' }}
        >
          <Toolbar>
            <SectionWrapper>
              <Stack direction="row" gap={1} alignItems="center">
                <LogoContainer darkMode={variant === 'dark'} disableInteraction={disableLogoInteraction} />

                {title && !ToolbarProps && (
                  <>
                    <NavigationBarDivider sx={{ mr: 1 }} />
                    <TitleWrapper data-testid="NavigationBar-title">
                      <Typography variant="natter-text-sm" noWrap>
                        {title}
                      </Typography>
                    </TitleWrapper>
                  </>
                )}
              </Stack>
            </SectionWrapper>

            {displayUserSection && (
              <Stack direction="row" justifyContent="end" alignItems="center" gap={1}>
                <GoToHelpCenter />
                {isMobile ? (
                  <MobileMenuButton />
                ) : (
                  <>
                    <NavigationBarDivider sx={{ ml: 0.25, mr: 1 }} />
                    <CurrentUserContainer />
                  </>
                )}
              </Stack>
            )}

            {onClose && (
              <IconButton aria-label="Close" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            )}
          </Toolbar>

          {ToolbarProps && <NavigationBarToolbar title={title} {...ToolbarProps} />}
          {children}
        </AppBar>
        {/**
         * This is needed so we don't have anything behind the fixed position app bar
         * https://mui.com/material-ui/react-app-bar/#fixed-placement
         * */}
        <Toolbar sx={{ height: size.height, flexShrink: 0, displayPrint: 'none' }} disableGutters />
      </>
    );
  }
);
